import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ContactModal } from "../../components/contactModal";

const serviceKeys = [
  {
    titleKey: "services.gameDev",
    descKey: "services.gameDevDesc",
    icon: "/controller.svg",
  },
  {
    titleKey: "services.customSoftware",
    descKey: "services.customSoftwareDesc",
    icon: "/laptop.svg",
  },
  {
    titleKey: "services.prototyping",
    descKey: "services.prototypingDesc",
    icon: "/rocket.svg",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.5, ease: "easeOut" },
  }),
};

export const Home = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  const textParts = [
    { text: t("hero.game"), color: "rgb(36, 233, 74)" },
    { text: t("hero.development"), color: "white" },
    { text: t("hero.studio"), color: "white" },
  ];

  return (
    <div className="flex flex-col w-full z-10">
      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center h-screen px-6 text-center gap-6 pt-20">
        <div className="flex gap-2 items-end text-white font-extrabold">
          <div className="text-5xl md:text-6xl xl:text-8xl uppercase">
            I made
          </div>
          <img
            src="/imab_logo_green.png"
            alt="Logo"
            className="h-10 md:h-12 xl:h-20"
          />
          <div className="text-5xl md:text-6xl xl:text-8xl uppercase">Bug</div>
        </div>

        <div className="flex gap-2 justify-center">
          {textParts.map((part, partIndex) => (
            <span
              key={partIndex}
              className="text-2xl md:text-3xl uppercase font-extrabold"
              style={{ color: part.color }}
            >
              {part.text.split("").map((char: string, charIndex: number) => (
                <motion.span
                  key={charIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    delay: (partIndex * 12 + charIndex) * 0.08,
                    duration: 0.05,
                  }}
                >
                  {char}
                </motion.span>
              ))}
            </span>
          ))}
        </div>

        <motion.p
          className="text-gray-300 text-lg md:text-xl max-w-2xl mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.6 }}
        >
          {t("hero.tagline")}
        </motion.p>

        <motion.div
          className="flex gap-4 mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.6, duration: 0.5 }}
        >
          <button
            onClick={() => setModalOpen(true)}
            className="bg-green-indie hover:bg-green-600 text-black font-bold py-3 px-8 rounded-lg transition-all hover:scale-105 active:scale-95 text-lg"
          >
            {t("hero.cta")}
          </button>
          <Link
            to="/portfolio"
            className="border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white/10 transition-all hover:scale-105 active:scale-95 text-lg"
          >
            {t("hero.seeWork")}
          </Link>
        </motion.div>

        {/* Scroll down arrow */}
        <motion.button
          onClick={() =>
            document
              .getElementById("whatWeBuild")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="mt-8 text-white/60 hover:text-green-indie transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 8, 0] }}
          transition={{
            opacity: { delay: 3, duration: 0.5 },
            y: { delay: 3, duration: 1.5, repeat: Infinity, ease: "easeInOut" },
          }}
          aria-label="Scroll down"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 13l5 5 5-5" />
            <path d="M7 6l5 5 5-5" />
          </svg>
        </motion.button>
      </section>

      {/* Services Section */}
      <section id="whatWeBuild" className="pt-16 pb-20 px-6 w-full bg-white">
        <motion.h2
          className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center mb-12 uppercase"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t("services.title")}{" "}
          <span className="text-green-indie">
            {t("services.titleHighlight")}
          </span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {serviceKeys.map((service, i) => (
            <motion.div
              key={service.titleKey}
              className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-8 text-center hover:border-green-indie/50 shadow-lg transition-colors"
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center">
                <img
                  src={service.icon}
                  alt={t(service.titleKey)}
                  className="text-4xl mb-4 w-12"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {t(service.titleKey)}
              </h3>
              <p className="text-gray-600">{t(service.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio Teaser */}
      <section id="portfolio" className="py-20 px-6 w-full bg-white">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 uppercase">
            {t("portfolio.title")}{" "}
            <span className="text-green-indie">
              {t("portfolio.titleHighlight")}
            </span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            {t("portfolio.description")}
          </p>
          <Link
            to="/portfolio"
            className="inline-block bg-green-indie hover:bg-green-600 text-black font-bold py-3 px-8 rounded-lg transition-all hover:scale-105 active:scale-95 text-lg"
          >
            {t("portfolio.cta")}
          </Link>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-20 px-6 w-full bg-white">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 uppercase">
            {t("cta.title")}{" "}
            <span className="text-green-indie">{t("cta.titleHighlight")}</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8">{t("cta.description")}</p>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-block bg-green-indie hover:bg-green-600 text-black font-bold py-4 px-10 rounded-lg transition-all hover:scale-105 active:scale-95 text-xl"
          >
            {t("cta.button")}
          </button>
        </motion.div>
      </section>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};
